import { OFFICIAL_SUPPORT_ROLES } from '../constants';
import { EventOption, LocationType, ScheduleItem } from '../types';

export interface OfficialSupportDayOption {
  value: string;
  label: string;
  shortLabel: string;
}

const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'];
const DEFAULT_WEEKEND_CLINIC_DAYS = new Set([0, 6]); // 週日、週六

const pad2 = (value: number) => String(value).padStart(2, '0');

const toIsoDate = (date: Date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const parseIsoDate = (value?: string): Date | null => {
  if (!value) return null;
  const normalized = value.trim().replace(/\//g, '-');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return null;
  const date = new Date(`${normalized}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toOption = (date: Date): OfficialSupportDayOption => {
  const month = pad2(date.getMonth() + 1);
  const day = pad2(date.getDate());
  const weekday = WEEKDAYS[date.getDay()];

  return {
    value: toIsoDate(date),
    label: `${month}/${day}（週${weekday}）`,
    shortLabel: `${month}/${day}（${weekday}）`
  };
};

const enumerateDateRange = (start: Date, end: Date): Date[] => {
  const dates: Date[] = [];
  const cursor = new Date(start);
  cursor.setHours(0, 0, 0, 0);
  const last = new Date(end);
  last.setHours(0, 0, 0, 0);

  while (cursor <= last) {
    dates.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return dates;
};

const parseDateRange = (event: EventOption): Date[] => {
  const start = parseIsoDate(event.startDate);
  const end = parseIsoDate(event.endDate) ?? start;
  if (start && end) {
    return enumerateDateRange(start, end);
  }

  const range = (event.dateRange || '').trim();
  const sameMonthMatch = range.match(/(\d{1,2})月(\d{1,2})(?:日)?\s*(?:-|－|–|—|到|至|~)\s*(\d{1,2})日/);
  if (sameMonthMatch) {
    const month = Number(sameMonthMatch[1]);
    const startDay = Number(sameMonthMatch[2]);
    const endDay = Number(sameMonthMatch[3]);
    return enumerateDateRange(
      new Date(event.year, month - 1, startDay),
      new Date(event.year, month - 1, endDay)
    );
  }

  const crossMonthMatch = range.match(/(\d{1,2})月(\d{1,2})日\s*(?:-|－|–|—|到|至|~)\s*(\d{1,2})月(\d{1,2})日/);
  if (crossMonthMatch) {
    return enumerateDateRange(
      new Date(event.year, Number(crossMonthMatch[1]) - 1, Number(crossMonthMatch[2])),
      new Date(event.year, Number(crossMonthMatch[3]) - 1, Number(crossMonthMatch[4]))
    );
  }

  const singleDateMatch = range.match(/(\d{1,2})月(\d{1,2})日/);
  if (singleDateMatch) {
    return [new Date(event.year, Number(singleDateMatch[1]) - 1, Number(singleDateMatch[2]))];
  }

  return [];
};

const parseScheduleDates = (event: EventOption, scheduleItem: ScheduleItem): Date[] => {
  const raw = scheduleItem.date
    .trim()
    .replace(/[（(][^）)]*[）)]/g, '')
    .replace(/\s+/g, '');

  if (!raw) return [];

  const iso = parseIsoDate(raw);
  if (iso) return [iso];

  const fullIsoRange = raw.match(/^(\d{4}[/-]\d{1,2}[/-]\d{1,2})(?:~|至|到|－|–|—|\s-\s)(\d{4}[/-]\d{1,2}[/-]\d{1,2})$/);
  if (fullIsoRange) {
    const start = parseIsoDate(fullIsoRange[1]);
    const end = parseIsoDate(fullIsoRange[2]);
    return start && end ? enumerateDateRange(start, end) : [];
  }

  const slashRange = raw.match(/^(\d{1,2})[/-](\d{1,2})(?:~|至|到|－|–|—|-)(\d{1,2})[/-](\d{1,2})$/);
  if (slashRange) {
    return enumerateDateRange(
      new Date(event.year, Number(slashRange[1]) - 1, Number(slashRange[2])),
      new Date(event.year, Number(slashRange[3]) - 1, Number(slashRange[4]))
    );
  }

  const slashSameMonthRange = raw.match(/^(\d{1,2})[/-](\d{1,2})(?:~|至|到|－|–|—|-)(\d{1,2})$/);
  if (slashSameMonthRange) {
    return enumerateDateRange(
      new Date(event.year, Number(slashSameMonthRange[1]) - 1, Number(slashSameMonthRange[2])),
      new Date(event.year, Number(slashSameMonthRange[1]) - 1, Number(slashSameMonthRange[3]))
    );
  }

  const chineseRange = raw.match(/^(\d{1,2})月(\d{1,2})(?:日)?(?:~|至|到|－|–|—|-)(?:(\d{1,2})月)?(\d{1,2})日?$/);
  if (chineseRange) {
    const startMonth = Number(chineseRange[1]);
    const endMonth = Number(chineseRange[3] ?? chineseRange[1]);

    return enumerateDateRange(
      new Date(event.year, startMonth - 1, Number(chineseRange[2])),
      new Date(event.year, endMonth - 1, Number(chineseRange[4]))
    );
  }

  const slashSingle = raw.match(/^(\d{1,2})[/-](\d{1,2})$/);
  if (slashSingle) {
    return [new Date(event.year, Number(slashSingle[1]) - 1, Number(slashSingle[2]))];
  }

  const chineseSingle = raw.match(/^(\d{1,2})月(\d{1,2})日?$/);
  if (chineseSingle) {
    return [new Date(event.year, Number(chineseSingle[1]) - 1, Number(chineseSingle[2]))];
  }

  return [];
};

const isClinicSchedule = (item: ScheduleItem) => {
  const text = `${item.activity} ${item.notes ?? ''} ${item.location}`.toLowerCase();
  return item.locationType === LocationType.Clinic || text.includes('義診');
};

const NON_CLINIC_EVENT_PATTERN = /動員|聚會|訓練|同工|年會/;

const dedupeAndSortOptions = (options: OfficialSupportDayOption[]) => {
  const byValue = new Map<string, OfficialSupportDayOption>();
  options.forEach(option => byValue.set(option.value, option));
  return [...byValue.values()].sort((a, b) => a.value.localeCompare(b.value));
};

const normalizeRoleName = (role: string) =>
  role
    .split('~')[0]
    .split(',')[0]
    .split('(')[0]
    .split('（')[0]
    .trim();

export const isOfficialSupportRole = (role: string) =>
  OFFICIAL_SUPPORT_ROLES.includes(normalizeRoleName(role));

export const isOfficialSupportEvent = (event: EventOption): boolean => {
  const explicitSetting = (event as EventOption & { requiresOfficialSupport?: boolean }).requiresOfficialSupport;
  if (typeof explicitSetting === 'boolean') return explicitSetting;

  if (event.schedule && event.schedule.length > 0) {
    return event.schedule.some(isClinicSchedule);
  }

  const eventText = `${event.location} ${event.description ?? ''}`;
  if (NON_CLINIC_EVENT_PATTERN.test(eventText) && !eventText.includes('義診')) {
    return false;
  }

  return getOfficialSupportDayOptions(event).length > 0;
};

export const getOfficialSupportDayOptions = (event: EventOption): OfficialSupportDayOption[] => {
  const scheduleDates = event.schedule
    ?.filter(isClinicSchedule)
    .flatMap(item => parseScheduleDates(event, item))
    .map(toOption) ?? [];

  if (scheduleDates.length > 0) {
    return dedupeAndSortOptions(scheduleDates);
  }

  if (event.schedule && event.schedule.length > 0) {
    return [];
  }

  const rangeOptions = parseDateRange(event).map(toOption);
  if (rangeOptions.length <= 2) {
    return dedupeAndSortOptions(rangeOptions);
  }

  const likelyClinicOptions = rangeOptions.filter(option => {
    const date = parseIsoDate(option.value);
    return date ? DEFAULT_WEEKEND_CLINIC_DAYS.has(date.getDay()) : false;
  });

  return dedupeAndSortOptions(likelyClinicOptions.length > 0 ? likelyClinicOptions : rangeOptions);
};

export const formatOfficialSupportAvailability = (
  event: EventOption,
  selectedDays: string[] = []
): string => {
  const options = getOfficialSupportDayOptions(event);
  const selectedSet = new Set(selectedDays);
  const selectedOptions = options.filter(option => selectedSet.has(option.value));

  if (selectedOptions.length === 0) {
    return '';
  }

  const labels = selectedOptions.map(option => option.shortLabel).join('、');
  if (selectedOptions.length === options.length && options.length > 1) {
    return `都可（${labels}）`;
  }

  return labels;
};
